import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { format } from "date-fns";
import { 
  CalendarIcon, Plus, Pencil, Trash2, Package, 
  Truck, CheckCircle, Clock, Search, X, Ship
} from "lucide-react";
import { cn } from "@/lib/utils";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_OPTIONS = [
  { value: "Pending", label: "Pending", color: "bg-gray-500" },
  { value: "In Transit", label: "In Transit", color: "bg-blue-500" },
  { value: "Arrived", label: "Arrived", color: "bg-green-500" },
  { value: "Cancelled", label: "Cancelled", color: "bg-red-500" },
];

const initialFormState = {
  item_name: "",
  item_code: "",
  quantity: 0,
  expected_arrival_date: null,
  supplier: "",
  notes: "",
  cargo_number: "",
  status: "Pending",
};

export default function Receiving() {
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [form, setForm] = useState(initialFormState);
  const [editingRecord, setEditingRecord] = useState(null);
  const [calendarOpen, setCalendarOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [items, setItems] = useState([]);
  const [itemSuggestions, setItemSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);

  // Fetch receiving records
  const fetchRecords = useCallback(async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem("token");
      const params = new URLSearchParams();
      if (statusFilter !== "all") params.append("status", statusFilter);
      if (searchQuery) params.append("item_name", searchQuery);
      
      const res = await axios.get(`${API}/receiving?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setRecords(res.data);
    } catch (err) {
      console.error("Error fetching receiving records:", err);
      toast.error("Failed to fetch receiving records");
    } finally {
      setLoading(false);
    }
  }, [statusFilter, searchQuery]);

  // Fetch items for autocomplete
  const fetchItems = useCallback(async () => {
    try {
      const token = localStorage.getItem("token");
      const res = await axios.get(`${API}/item-master/list?limit=500`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setItems(res.data.items || []);
    } catch (err) {
      console.error("Error fetching items:", err);
    }
  }, []);

  useEffect(() => {
    fetchRecords();
    fetchItems();
  }, [fetchRecords, fetchItems]);

  // Handle item name input change
  const handleItemNameChange = (value) => {
    setForm(prev => ({ ...prev, item_name: value }));
    
    if (value.length > 1) {
      const filtered = items.filter(item => 
        item.item_name.toLowerCase().includes(value.toLowerCase())
      );
      setItemSuggestions(filtered.slice(0, 10));
      setShowSuggestions(true);
    } else {
      setShowSuggestions(false);
    }
  };

  // Select item from suggestions
  const selectItem = (item) => {
    setForm(prev => ({ 
      ...prev, 
      item_name: item.item_name,
      item_code: item.item_code || ""
    }));
    setShowSuggestions(false);
  };

  // Handle date select
  const handleDateSelect = (date) => {
    setForm(prev => ({ ...prev, expected_arrival_date: date }));
    setCalendarOpen(false);
  };

  // Create new receiving record
  const handleCreate = async () => {
    if (!form.item_name.trim()) {
      toast.error("Item name is required");
      return;
    }
    if (form.quantity <= 0) {
      toast.error("Quantity must be greater than 0");
      return;
    }
    if (!form.expected_arrival_date) {
      toast.error("Expected arrival date is required");
      return;
    }

    try {
      const token = localStorage.getItem("token");
      const payload = {
        ...form,
        expected_arrival_date: format(form.expected_arrival_date, "yyyy-MM-dd")
      };
      
      await axios.post(`${API}/receiving`, payload, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success("Receiving record created successfully!");
      setForm(initialFormState);
      setShowAddModal(false);
      fetchRecords();
    } catch (err) {
      console.error("Error creating receiving record:", err);
      toast.error(err.response?.data?.detail || "Failed to create receiving record");
    }
  };

  // Update receiving record
  const handleUpdate = async () => {
    if (!editingRecord) return;

    try {
      const token = localStorage.getItem("token");
      const payload = {
        ...form,
        expected_arrival_date: form.expected_arrival_date 
          ? format(form.expected_arrival_date, "yyyy-MM-dd") 
          : editingRecord.expected_arrival_date
      };
      
      await axios.put(`${API}/receiving/${editingRecord.id}`, payload, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success("Receiving record updated!");
      setShowEditModal(false);
      setEditingRecord(null);
      setForm(initialFormState);
      fetchRecords();
    } catch (err) {
      console.error("Error updating receiving record:", err);
      toast.error("Failed to update receiving record");
    }
  };

  // Delete receiving record
  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this receiving record?")) return;

    try {
      const token = localStorage.getItem("token");
      await axios.delete(`${API}/receiving/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Receiving record deleted");
      fetchRecords();
    } catch (err) {
      console.error("Error deleting receiving record:", err);
      toast.error("Failed to delete receiving record");
    }
  };

  // Mark as arrived
  const handleMarkArrived = async (id) => {
    try {
      const token = localStorage.getItem("token");
      await axios.post(`${API}/receiving/${id}/mark-arrived`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Marked as arrived! Now add to inventory via Receive Stock.");
      fetchRecords();
    } catch (err) {
      console.error("Error marking as arrived:", err);
      toast.error("Failed to mark as arrived");
    }
  };

  // Open edit modal
  const openEditModal = (record) => {
    setEditingRecord(record);
    setForm({
      item_name: record.item_name,
      item_code: record.item_code,
      quantity: record.quantity,
      expected_arrival_date: record.expected_arrival_date ? new Date(record.expected_arrival_date) : null,
      supplier: record.supplier,
      notes: record.notes,
      cargo_number: record.cargo_number,
      status: record.status,
    });
    setShowEditModal(true);
  };

  // Calculate summary
  const summary = {
    pending: records.filter(r => r.status === "Pending").length,
    inTransit: records.filter(r => r.status === "In Transit").length,
    arrived: records.filter(r => r.status === "Arrived").length,
    totalQuantity: records.filter(r => r.status !== "Cancelled" && r.status !== "Arrived")
      .reduce((sum, r) => sum + r.quantity, 0)
  };

  return (
    <div className="space-y-6" data-testid="receiving-page">
      {/* Page Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <Ship className="h-7 w-7 text-blue-600" />
            Receiving (Incoming Stock)
          </h1>
          <p className="text-gray-500 mt-1">Track incoming stocks that are still in transit</p>
        </div>
        <Button onClick={() => setShowAddModal(true)} className="bg-blue-600 hover:bg-blue-700">
          <Plus className="h-4 w-4 mr-1" />
          Add Incoming Stock
        </Button>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="bg-gray-50 border-gray-200">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <Clock className="h-8 w-8 text-gray-500" />
              <div>
                <div className="text-2xl font-bold text-gray-700">{summary.pending}</div>
                <div className="text-xs text-gray-500">Pending</div>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-blue-50 border-blue-200">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <Truck className="h-8 w-8 text-blue-500" />
              <div>
                <div className="text-2xl font-bold text-blue-700">{summary.inTransit}</div>
                <div className="text-xs text-blue-500">In Transit</div>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-green-50 border-green-200">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <CheckCircle className="h-8 w-8 text-green-500" />
              <div>
                <div className="text-2xl font-bold text-green-700">{summary.arrived}</div>
                <div className="text-xs text-green-500">Arrived</div>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-amber-50 border-amber-200">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <Package className="h-8 w-8 text-amber-500" />
              <div>
                <div className="text-2xl font-bold text-amber-700">{summary.totalQuantity.toLocaleString()}</div>
                <div className="text-xs text-amber-500">Total Incoming Units</div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-wrap gap-4 items-center">
            <div className="flex-1 min-w-[200px]">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                <Input
                  placeholder="Search item name..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10"
                />
              </div>
            </div>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-[180px]">
                <SelectValue placeholder="Filter by status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                {STATUS_OPTIONS.map(opt => (
                  <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Records Table */}
      <Card>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 border-b">
                <tr>
                  <th className="px-4 py-3 text-left font-semibold">Status</th>
                  <th className="px-4 py-3 text-left font-semibold">Item</th>
                  <th className="px-4 py-3 text-center font-semibold">Quantity</th>
                  <th className="px-4 py-3 text-center font-semibold">Expected Arrival</th>
                  <th className="px-4 py-3 text-left font-semibold">Cargo #</th>
                  <th className="px-4 py-3 text-left font-semibold">Supplier</th>
                  <th className="px-4 py-3 text-left font-semibold">Notes</th>
                  <th className="px-4 py-3 text-center font-semibold">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {loading ? (
                  <tr>
                    <td colSpan={8} className="px-4 py-8 text-center text-gray-500">
                      Loading...
                    </td>
                  </tr>
                ) : records.length === 0 ? (
                  <tr>
                    <td colSpan={8} className="px-4 py-8 text-center text-gray-500">
                      <Ship className="h-12 w-12 mx-auto mb-2 opacity-30" />
                      No receiving records found
                    </td>
                  </tr>
                ) : (
                  records.map(record => {
                    const statusOpt = STATUS_OPTIONS.find(s => s.value === record.status);
                    return (
                      <tr key={record.id} className="hover:bg-gray-50">
                        <td className="px-4 py-3">
                          <Badge className={`${statusOpt?.color || 'bg-gray-500'} text-white`}>
                            {record.status}
                          </Badge>
                        </td>
                        <td className="px-4 py-3">
                          <div className="font-medium text-gray-900">{record.item_name}</div>
                          {record.item_code && (
                            <div className="text-xs text-gray-500">{record.item_code}</div>
                          )}
                        </td>
                        <td className="px-4 py-3 text-center">
                          <span className="font-bold text-blue-600">{record.quantity.toLocaleString()}</span>
                        </td>
                        <td className="px-4 py-3 text-center">
                          <span className="font-mono text-sm">{record.expected_arrival_date}</span>
                        </td>
                        <td className="px-4 py-3 font-mono text-xs">{record.cargo_number || "-"}</td>
                        <td className="px-4 py-3 text-gray-600">{record.supplier || "-"}</td>
                        <td className="px-4 py-3 text-gray-500 text-xs max-w-[200px] truncate">
                          {record.notes || "-"}
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex items-center justify-center gap-1">
                            {record.status !== "Arrived" && record.status !== "Cancelled" && (
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => handleMarkArrived(record.id)}
                                className="text-green-600 hover:text-green-700 hover:bg-green-50"
                              >
                                <CheckCircle className="h-4 w-4" />
                              </Button>
                            )}
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => openEditModal(record)}
                            >
                              <Pencil className="h-4 w-4" />
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleDelete(record.id)}
                              className="text-red-600 hover:text-red-700 hover:bg-red-50"
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* Add Modal */}
      <Dialog open={showAddModal} onOpenChange={setShowAddModal}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Ship className="h-5 w-5 text-blue-600" />
              Add Incoming Stock
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            {/* Item Name with Autocomplete */}
            <div className="relative">
              <Label>Item Name *</Label>
              <Input
                value={form.item_name}
                onChange={(e) => handleItemNameChange(e.target.value)}
                onFocus={() => form.item_name.length > 1 && setShowSuggestions(true)}
                onBlur={() => setTimeout(() => setShowSuggestions(false), 200)}
                placeholder="Type item name..."
              />
              {showSuggestions && itemSuggestions.length > 0 && (
                <div className="absolute z-50 w-full mt-1 bg-white border rounded-md shadow-lg max-h-48 overflow-auto">
                  {itemSuggestions.map((item, idx) => (
                    <button
                      key={idx}
                      type="button"
                      className="w-full px-3 py-2 text-left text-sm hover:bg-blue-50"
                      onMouseDown={() => selectItem(item)}
                    >
                      <div className="font-medium">{item.item_name}</div>
                      {item.item_code && <div className="text-xs text-gray-500">{item.item_code}</div>}
                    </button>
                  ))}
                </div>
              )}
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Quantity *</Label>
                <Input
                  type="number"
                  value={form.quantity}
                  onChange={(e) => setForm(prev => ({ ...prev, quantity: parseFloat(e.target.value) || 0 }))}
                  min="0"
                  step="1"
                />
              </div>
              <div>
                <Label>Expected Arrival Date *</Label>
                <Popover open={calendarOpen} onOpenChange={setCalendarOpen}>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      className={cn(
                        "w-full justify-start text-left font-normal",
                        !form.expected_arrival_date && "text-muted-foreground"
                      )}
                    >
                      <CalendarIcon className="mr-2 h-4 w-4" />
                      {form.expected_arrival_date ? (
                        format(form.expected_arrival_date, "PPP")
                      ) : (
                        <span>Pick a date</span>
                      )}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <Calendar
                      mode="single"
                      selected={form.expected_arrival_date}
                      onSelect={handleDateSelect}
                      initialFocus
                    />
                  </PopoverContent>
                </Popover>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Cargo #</Label>
                <Input
                  value={form.cargo_number}
                  onChange={(e) => setForm(prev => ({ ...prev, cargo_number: e.target.value }))}
                  placeholder="e.g., CARGO-2026-001"
                  className="font-mono"
                />
              </div>
              <div>
                <Label>Supplier</Label>
                <Input
                  value={form.supplier}
                  onChange={(e) => setForm(prev => ({ ...prev, supplier: e.target.value }))}
                  placeholder="Supplier name"
                />
              </div>
            </div>

            <div>
              <Label>Notes</Label>
              <Textarea
                value={form.notes}
                onChange={(e) => setForm(prev => ({ ...prev, notes: e.target.value }))}
                placeholder="Optional notes about this shipment..."
                rows={2}
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddModal(false)}>
              Cancel
            </Button>
            <Button onClick={handleCreate} className="bg-blue-600 hover:bg-blue-700">
              <Plus className="h-4 w-4 mr-1" />
              Add Receiving
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Modal */}
      <Dialog open={showEditModal} onOpenChange={setShowEditModal}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Pencil className="h-5 w-5 text-blue-600" />
              Edit Receiving Record
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            <div>
              <Label>Item Name</Label>
              <Input value={form.item_name} disabled className="bg-gray-50" />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Quantity</Label>
                <Input
                  type="number"
                  value={form.quantity}
                  onChange={(e) => setForm(prev => ({ ...prev, quantity: parseFloat(e.target.value) || 0 }))}
                  min="0"
                />
              </div>
              <div>
                <Label>Status</Label>
                <Select value={form.status} onValueChange={(v) => setForm(prev => ({ ...prev, status: v }))}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {STATUS_OPTIONS.map(opt => (
                      <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Expected Arrival</Label>
                <Popover open={calendarOpen} onOpenChange={setCalendarOpen}>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      className={cn(
                        "w-full justify-start text-left font-normal",
                        !form.expected_arrival_date && "text-muted-foreground"
                      )}
                    >
                      <CalendarIcon className="mr-2 h-4 w-4" />
                      {form.expected_arrival_date ? (
                        format(form.expected_arrival_date, "PPP")
                      ) : (
                        <span>Pick a date</span>
                      )}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <Calendar
                      mode="single"
                      selected={form.expected_arrival_date}
                      onSelect={handleDateSelect}
                      initialFocus
                    />
                  </PopoverContent>
                </Popover>
              </div>
              <div>
                <Label>Cargo #</Label>
                <Input
                  value={form.cargo_number}
                  onChange={(e) => setForm(prev => ({ ...prev, cargo_number: e.target.value }))}
                  className="font-mono"
                />
              </div>
            </div>

            <div>
              <Label>Notes</Label>
              <Textarea
                value={form.notes}
                onChange={(e) => setForm(prev => ({ ...prev, notes: e.target.value }))}
                rows={2}
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEditModal(false)}>
              Cancel
            </Button>
            <Button onClick={handleUpdate} className="bg-blue-600 hover:bg-blue-700">
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
